import type { SystemMetadata } from '@life-os/app-core';
import { MaturityChip, Pane, PlatformChips, PrivacyChip } from '@life-os/ui';
import { useRegistry } from '../app/store.js';
import { useRouter } from '../app/router.js';
import { Icon, iconNameForRoute } from '../app/icons.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

function findByRoute(systems: readonly SystemMetadata[], path: string): SystemMetadata | undefined {
  return systems.find((s) => s.surface.kind === 'route' && s.surface.path === path);
}

const GROUP_LABEL: Record<string, string> = {
  daily: 'Daily', direction: 'Direction', repair: 'Repair', projects: 'Projects',
  knowledge: 'Knowledge', study: 'Study', career: 'Career', health: 'Health',
  finance: 'Finance', business: 'Business', work: 'Work', mental: 'Mental',
  spiritual: 'Spiritual', environment: 'Environment', assistant: 'Assistant',
  bridges: 'Connections', runtime: 'System',
};

/**
 * Registry-driven hub for any system without a bespoke screen yet. It presents
 * the system with intent — a clear header, what it's for, and the connected
 * systems as navigable cards — so the area feels designed while its deep
 * features are built out.
 */
export function DomainHub() {
  const registry = useRegistry();
  const { path, navigate } = useRouter();
  const all = registry.all();
  const sys = findByRoute(all, path);

  if (!sys) {
    return (
      <Pane title="Unknown screen">
        <div style={{ color: 'var(--lc-on-surface-variant)' }}>This route is not a registered system.</div>
      </Pane>
    );
  }

  const related = all.filter((s) => s.group === sys.group && s.id !== sys.id);
  const iconName = iconNameForRoute(sys.surface.kind === 'route' ? sys.surface.path : undefined);

  return (
    <>
      <ScreenHeader
        title={sys.label}
        subtitle={sys.shortPurpose}
        icon={iconName}
        actions={<><MaturityChip value={sys.maturity} /><PrivacyChip value={sys.privacyClass} /></>}
      />

      <Pane title="Overview" hint={`${GROUP_LABEL[sys.group] ?? sys.group} · local-first`}>
        <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)', lineHeight: 1.6 }}>
          {sys.shortPurpose} Everything here stays on your device by default. This space brings the{' '}
          {GROUP_LABEL[sys.group]?.toLowerCase() ?? sys.group} pieces of your life into one calm view —
          its deeper tools land incrementally, and the connected systems below are ready now.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
          <PlatformChips availability={sys.platformAvailability} />
          {sys.sourcePreservation === 'preserve-before-promote' ? (
            <span className="lc-chip">Source preserved before promotion</span>
          ) : null}
        </div>
      </Pane>

      {related.length > 0 ? (
        <Pane title={`Connected in ${GROUP_LABEL[sys.group] ?? sys.group}`} hint="Jump to a related system">
          <div className="hub-grid">
            {related.map((r) => {
              const route = r.surface.kind === 'route' ? r.surface.path : undefined;
              return (
                <button
                  key={String(r.id)}
                  className="hub-card"
                  onClick={route ? () => navigate(route) : undefined}
                  disabled={!route}
                >
                  <span className="hub-card__icon">
                    <Icon name={iconNameForRoute(route)} size={20} />
                  </span>
                  <span className="hub-card__title">{r.label}</span>
                  <span className="hub-card__sub">{r.shortPurpose}</span>
                </button>
              );
            })}
          </div>
        </Pane>
      ) : null}
    </>
  );
}
